<script setup>
import { computed, inject } from 'vue';
import { initials, styleVars } from './shared.js';
import { socialLinks } from '../socialIcons.js';
import { footerColumns } from '../menuLinks.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});
const editing = inject('tiqEditorCanvas', false);



// Less any link to the Trails page while it has nothing to list (menuLinks.js).
const columns = computed(() => footerColumns(props.config.columns, props.site_data, editing));
const social = computed(() => socialLinks(props.config, props.site_data));
const eyebrow = computed(() => String(props.config.social_eyebrow ?? '').trim());
const mark = computed(() => initials(props.org?.name));
const copyright = computed(() => props.config.copyright || `© ${new Date().getFullYear()} ${props.org?.name || ''}`.trim());
</script>

<template>
    <footer :id="config.anchor_id || null" class="th-block th-footer th-dark" :style="styleVars(config)">
        <div class="th-wrap th-footer__cols">
            <div class="th-footer__brand">
                <div class="th-footer__id">
                    <img v-if="org.logo_url" :src="org.logo_url" alt="" class="th-footer__logo">
                    <span v-else class="th-footer__mark">{{ mark }}</span>
                    <span class="th-footer__name">{{ org.name }}</span>
                </div>
                <p v-if="config.tagline" class="th-footer__tagline">{{ config.tagline }}</p>
                <p v-if="eyebrow && social.length" class="th-footer__col-head th-footer__social-eyebrow" data-footer-social-eyebrow>{{ eyebrow }}</p>
                <div v-if="social.length" class="th-footer__social">
                    <a v-for="(s, i) in social" :key="i"
                       :href="s.href"
                       :target="s.newTab ? '_blank' : null"
                       rel="noopener"
                       :aria-label="s.label"
                       class="th-footer__social-link">
                        <img v-if="s.img" :src="s.img" alt="" class="tiq-social-go">
                        <svg v-else viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path :d="s.d" />
                        </svg>
                    </a>
                </div>
            </div>

            <div v-for="(col, ci) in columns" :key="ci" class="th-footer__col">
                <p v-if="col.heading" class="th-footer__col-head">{{ col.heading }}</p>
                <a v-for="(link, li) in (col.links || [])" :key="li" :href="link.href || '#'" class="th-footer__link">
                    {{ link.label }}
                </a>
            </div>
        </div>

        <div class="th-wrap th-footer__bottom">
            <span>{{ copyright }}</span>
            <a href="https://trailsiq.com" target="_blank" rel="noopener" class="th-footer__powered">Powered by TrailsIQ</a>
        </div>
    </footer>
</template>

<style scoped>
.th-footer__cols {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 32px;
    padding-top: clamp(40px, 6vw, 72px);
    padding-bottom: 32px;
}
.th-footer__brand { display: flex; flex-direction: column; gap: 12px; }
.th-footer__id { display: flex; align-items: center; gap: 10px; color: #fff; }
.th-footer__mark {
    width: 34px;
    height: 34px;
    flex: none;
    border-radius: var(--r);
    background: var(--accent);
    display: grid;
    place-items: center;
    font: 700 13px/1 var(--th-display);
    color: #fff;
}
/* The logo is whatever shape the org uploaded, so it keeps its own
   proportions: a square crop with rounded corners cut the top and
   bottom off a tall mark. Same rule as the header logo. */
.th-footer__logo { height: 40px; width: auto; max-width: 160px; flex: none; object-fit: contain; }
.th-footer__name { font: 700 16px/1.1 var(--th-display); }
.th-footer__tagline { margin: 0; font-size: 14px; color: #a9aea6; }
.th-footer__social { display: flex; gap: 10px; margin-top: 6px; }
.th-footer__brand .th-footer__social-eyebrow { margin: 8px 0 -4px; }
.th-footer__social-eyebrow + .th-footer__social { margin-top: 0; }
.th-footer__social-link {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, .2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #d8dcd5;
}
.th-footer__social-link svg { width: 15px; height: 15px; }
/* TrailsIQ Go's bubble: a filled mark rather than a glyph, so it is
   drawn larger to read at the same weight as the icons beside it. */
.tiq-social-go { display: block; width: 20px; height: 20px; object-fit: contain; }
.th-footer__social-link:hover { background: var(--accent); border-color: var(--accent); color: #fff; }

.th-footer__col { display: flex; flex-direction: column; gap: 8px; font-size: 15px; }
.th-footer__col-head {
    font: 500 11px/1 var(--th-mono);
    letter-spacing: .1em;
    text-transform: uppercase;
    color: #8d928a;
    margin: 0 0 6px;
}
.th-footer__link { color: inherit; }

.th-footer__bottom {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
    padding-top: 20px;
    padding-bottom: calc(28px + env(safe-area-inset-bottom));
    border-top: 1px solid #33362f;
    font: 400 11px/1.4 var(--th-mono);
    letter-spacing: .04em;
    color: #8d928a;
}
.th-footer__powered { color: inherit; }
</style>
